import { Component, Element, Prop, Host, Listen, State, h } from "@stencil/core";
import { AppStateType, AppStoreType, buildEvent } from "../../utils/utils";
import { format } from "timeago.js";
import { hexToRGBA , getSize, getStyle } from "../../utils/utils";
import Fragment from "../../utils/fragment";
import { getItemTabIndex, isWidgetDisabled } from "../../store/getters";
import { setSliderIndex, setItemInFocus } from "../../store/slider";
import { BadgeHoverVerified, BadgeVerified, StarRating } from "../../utils/funcComponents";
import { Unsubscribe } from "../../utils/types";
import { basicRatio, modernRatio } from "../covet-pics-crop-text/crop-settings";

@Component({
  tag: "covet-pics-gallery-item-detail",
  styleUrl: "covet-pics-gallery-item-detail.scss"
})
export class CovetPicsGalleryItemDetail {
  @Element() el: HTMLCovetPicsGalleryItemDetailElement;

  /**
   * Image ALT tag
  */
  @Prop() altTag: string = "";

  /**
   * if item is highlighted
  */
  @Prop() highlighted: boolean = false;

  /**
   * if item is verified
  */
  @Prop() verified: boolean = false;

  /**
   * URL to the image that will be displayed
   */
  @Prop() imageUrl: string = null;

  /**
   * URL to the hi-res. image that will be displayed
   */
  @Prop() imageHighResolutionUrl: string = null;

  /**
   * Responsive sizes settings - hint for browser to load proper resolution image
   */
  @Prop() imageResponsiveSizes: string;

  /**
   * Unique `ID`, this value is used to open `CovetPopup`
  */
  @Prop() itemId: string;

  /**
   * Where the item is coming from (`instagram`, `upload_widget`, `file`)
  */
  @Prop() itemSource: string = "instagram";

  /**
   * item type (image, video, carousel, text)
  */
  @Prop() itemType: string;

  /**
   * links for this post (can be product or general link)
   */
  @Prop() links: Array<any>;

  /**
   * Link to Instagram post
  */
  @Prop() url: string;

  /**
   * Star rating
   */
  @Prop() rating: number = 0;

  /**
   * User name
   */
  @Prop() userName: string;

  /**
   * Caption text (comment)
  */
  @Prop() caption: string = "";

  /**
   * Created time
  */
  @Prop() createdTime: string;

  /**
   * item index in slider
  */
  @Prop() itemIndex: number;

  /**
   * App store
   */
  @Prop() appStore: AppStoreType;

  // App state;
  private appState: AppStateType;
  private unsubscribe: Unsubscribe;
  private itemStyle: string;
  private layout: string; // short var name (slider, grid, collage)

  @State() tabIndex: number = 0;

  @State() captionCSS: { [key: string]: string; } = {};

  @State() reviewClass: string = "";
  @State() starOnlyClass: string = "";
  @State() highlightedClass: string = "";


  @Listen("updateUploadWidgetCover", { capture: true, target: "window" })
  updateUploadWidgetCoverHandler(event) {
    if (this.itemSource === "upload_widget") {
      const previewImage = event.detail.image;

      if (previewImage === null) {
        this.imageUrl = undefined;
      } else {
        this.imageUrl = previewImage;
      }
    }
  }

  @Listen("elementResize", { target: "window" })
  elementResizeHandler() {
    this.getCaptionCollageCSS();
  }

  componentWillLoad() {
    this.reviewClass = this.caption !== null && this.itemType === "text" ? "text-review" : "";
    this.starOnlyClass = this.caption === null && this.itemType === "text" ? "star-only" : "";
    this.highlightedClass = this.highlighted ? "highlighted" : "";
    this.initStore();
  }

  componentDidLoad() {
    window.addEventListener("scroll", this.scrollHandler, { passive: true });
    this.isInViewport();
  }

  disconnectedCallback() {
    this.unsubscribe();
    window.removeEventListener("scroll", this.scrollHandler);
  }
  
  private initStore() {
    this.appState = this.appStore.getState(); 
    this.itemStyle = this.appState.settings.item_style;
    this.layout = this.appState.settings.display_type;
    this.unsubscribe = this.appStore.subscribe(() => {
      const isModalsOpen = isWidgetDisabled(this.appState);
      const isModalsWillClose = isModalsOpen && !isWidgetDisabled(this.appStore.getState());
      const isItemHoverChange = this.appState.slider.itemInFocus !== this.appStore.getState().slider.itemInFocus;
      this.appState = this.appStore.getState(); 
      this.layout = this.appState.settings.display_type;
      this.itemStyle = this.appState.settings.item_style;
      this.tabIndex = getItemTabIndex(this.appState, this.itemIndex); // TODO refactor funciton (layout in appState)
      if (isModalsWillClose && this.appState.slider.realIndex === this.itemIndex) {
        this.itemSource === "upload_widget" ? this.el.focus() : (this.el.querySelector(".btn-item-wrap") as HTMLButtonElement).focus();
      }
      if (isItemHoverChange && this.itemIndex !== this.appState.slider.itemInFocus) {
        this.itemSource === "upload_widget" ? this.el.blur() : (this.el.querySelector(".btn-item-wrap") as HTMLButtonElement).blur();
      }
    });
  }

  private nextTick = (fn) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(fn);
    });
  }

  private scrollHandler = () => {
    this.isInViewport();
  }

  private isInViewport() {
    // temp old ver
    // const isElInViewport = size(this.el).top >= 0 && size(this.el).bottom <= (window.innerHeight || document.documentElement.clientHeight);
    // return isElInViewport
    //   ? this.el.classList.add("in-viewport")
    //   : this.el.classList.remove("in-viewport");
    const screenHeight = (this.appState.size.windowHeight || document.documentElement.clientHeight);
    const isElInViewport = getSize(this.el, "top") >= 0 && getSize(this.el, "top") <= screenHeight / 2;
    const items = Array.from(this.el.parentNode.querySelectorAll(".grid-item"));
    const isLastHighlighted = items.length > 1
      ? items[items.length - 1].classList.contains("highlighted")
      : false;

    if (isElInViewport) return this.el.classList.add("in-viewport"); // show for item in viewport

    if (this.el === items[items.length - 2] && !isLastHighlighted && getSize(this.el, "bottom") < screenHeight + 30) // show 2 last on bottom
      return this.el.classList.add("in-viewport");
    if (this.el === items[items.length - 1] && getSize(this.el, "bottom") < screenHeight + 30) // show last el on bottom
      return this.el.classList.add("in-viewport");

    return this.el.classList.remove("in-viewport");
  }

  private imageLoadedHandler = () => {
    const customEvent = buildEvent("imageLoaded");
    this.el.removeAttribute("is-loading");
    this.el.dispatchEvent(customEvent);
    this.nextTick(() => this.getCaptionCollageCSS());
  }

  private itemClickHandler = (event) => {
    if (event.type === "keydown" && event.keyCode !== 13 && event.keyCode !== 32) return false;
    if (this.appState.settings.popup_disable_modal) {
      if (!this.appState.settings.item_open_link) { return false; }

      if (this.links.length > 0) {
        const link = this.links[0];
        const context = link.new_tab ? "_blank" : "_self";
        window.open(link.link, context);
      } else if (typeof this.url !== "undefined" && this.itemSource === "instagram") {
        window.open(this.url, "_top");
      }
    } else {
      const customEvent = buildEvent("itemClicked", this);
      this.el.dispatchEvent(customEvent);
    }
  }

  private itemOnFocusHandler = () => {
    return ["grid", "collage"].includes(this.layout)
      ? this.appStore.dispatch(setSliderIndex(this.itemIndex))
      : true;
  }

  private itemOnHoverHandler = () => {
    return this.appStore.dispatch(setItemInFocus(this.itemIndex));
  }

  private getItemCSSVariables() {
    let cssVariables = {
      "--covet-pics-item-detail-primary-color": this.appState.settings.primary_color.toString(),
      "--covet-pics-item-detail-star-color": this.appState.settings.star_color.toString(),
    };

    let itemBgOpacity = 1;
    if (this.itemStyle === "modern-1") itemBgOpacity = 0.2;
    if (this.itemStyle === "modern-2") itemBgOpacity = 0.3; 
    if (this.itemStyle === "modern-3") itemBgOpacity = 0.4;
    cssVariables["--covet-pics-item-detail-background-color"] = hexToRGBA(this.appState.settings.background_color, itemBgOpacity);

    if (this.itemStyle === "modern-4") {
      cssVariables["--covet-pics-item-detail-bottom-color"] = hexToRGBA(this.appState.settings.primary_color, 0.5);
    }
    return cssVariables;
  }

  private getCaptionCollageCSS() {
    const top = this.el.querySelector(".description .top");
    const bottom = this.el.querySelector(".description .bottom");
    const description = this.el.querySelector(".description");
    const caption = this.el.querySelector(".description .caption");
    if (caption !== null) {
      const topHeight = getSize(top, "height");
      const topMB = getStyle(top, "margin-bottom");
      const bottomHeight = getSize(bottom, "height");
      const bottomMT = getStyle(bottom, "margin-top");
      const descriptionHeight = getSize(description, "height");
      const descriptionPT = getStyle(description, "padding-top");
      const bottomFullHeight = bottomHeight + bottomMT;
      let topFullHeight = topHeight + topMB;
      if (this.itemStyle === "modern-6") topFullHeight = 0;

      const captionMaxHeight = descriptionHeight - 2 * descriptionPT - topFullHeight - bottomFullHeight;
      const captionLH = getStyle(caption, "line-height");
      const captionMaxLines = Math.floor(captionMaxHeight / captionLH) > 0
        ? Math.floor(captionMaxHeight / captionLH)
        : 1;
      this.captionCSS = {
        "--covet-pics-item-detail-caption-max-height": `${captionMaxLines * captionLH}px`,
        "--covet-pics-item-detail-caption-max-lines": `${captionMaxLines}`
      };
    }

    return this.captionCSS;
  }

  private renderUploadWidget() {
    return (
      <Host class={`${this.itemStyle} upload-widget`} style={this.getItemCSSVariables()} is-loading 
        onClick={this.itemClickHandler} onFocus={this.itemOnFocusHandler} onMouseEnter={this.itemOnHoverHandler} onKeyDown={this.itemClickHandler} 
        tabindex={this.tabIndex} aria-hidden={`${this.tabIndex === -1}`}>
        <span class="info">
          {!this.imageUrl &&
            <div class="default-cover">
              <svg class="svg-plus" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M24 0v24h24v1.2H24V48h-1.2V25.2H0V24h22.8V0H24z" fill="#464858" fill-rule="evenodd" /></svg>
              <span class="btn">{this.appState.settings.customer_photo_item_label}</span>
            </div>
          }
          {this.imageUrl &&
            <div class="custom-img-wrap">
              <img class="img" src={this.imageUrl} alt="upload image" onLoad={this.imageLoadedHandler} onError={this.imageLoadedHandler} />
            </div>
          }
        </span>
      </Host>
    );
  }

  private renderUserAndDate() {
    return (
      <Fragment>
        <h3 class={`username ${this.itemStyle} ${this.reviewClass} ${this.starOnlyClass}`}>{this.userName}</h3>
        {this.userName && this.createdTime && <span class={`slash ${this.itemStyle} ${this.reviewClass} ${this.starOnlyClass}`}>/</span>}
        <span class={`created-time ${this.itemStyle} ${this.reviewClass} ${this.starOnlyClass}`}>{format(this.createdTime)}</span>
      </Fragment>
    );
  }

  private renderRating() {
    if (this.rating === 0) return null;
    const numberRatingClass = ["basic-1", "basic-6", "modern-1", "modern-4", "modern-6"].includes(this.itemStyle) ? "numder-rating" : "";
    const classList = `${this.itemStyle} ${this.reviewClass} ${this.starOnlyClass} ${this.highlightedClass} ${numberRatingClass}`;
    return (
      <div class={`rating ${classList}`}>
        <span class={`icon-star active ${classList}`}>
          <svg class="svg-star" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" role="img"><title>star rating</title>
            <path d="M4.10511 3.85963C4.10511 3.85963 1.83465 4.12327 0.320057 4.29953C0.183543 4.31685 0.0628347 4.41251 0.0175692 4.55789C-0.0276963 4.70327 0.0175691 4.85467 0.118159 4.95033C1.24333 6.02672 2.93396 7.63867 2.93396 7.63867C2.93252 7.63867 2.46981 9.98349 2.16229 11.548C2.13642 11.6896 2.18529 11.8395 2.30312 11.9291C2.42023 12.0188 2.57112 12.0203 2.68967 11.9503C4.01458 11.1609 5.99907 9.97371 5.99907 9.97371C5.99907 9.97371 7.98429 11.1609 9.30703 11.951C9.42772 12.0203 9.57866 12.0188 9.69574 11.9291C9.8136 11.8395 9.86246 11.6896 9.83589 11.5488C9.52834 9.98349 9.06634 7.63867 9.06634 7.63867C9.06634 7.63867 10.757 6.02672 11.8821 4.95259C11.9828 4.85391 12.0273 4.70252 11.9828 4.55789C11.9382 4.41327 11.8175 4.31761 11.681 4.30103C10.1664 4.12327 7.8952 3.85963 7.8952 3.85963C7.8952 3.85963 6.95397 1.67673 6.32671 0.221454C6.2678 0.0911426 6.14422 0 5.99907 0C5.85394 0 5.72964 0.091896 5.67359 0.221454C5.04563 1.67673 4.10511 3.85963 4.10511 3.85963Z" />
          </svg>
        </span>
        <span class={`rating-data ${classList}`}>{this.rating}</span>
      </div>
    );
  }

  private renderBasicItem() {
    const hideCaption = (this.caption === null) || this.caption.length === 0 && this.highlighted;
    const starOnly = this.caption === null && this.itemType === "text"; // TODO need to refactor, move review split img-wrap and review to different render function
    const jsTextCut = Object.keys(this.captionCSS).length !== 0 ? "js-text-cut" : "";

    let showTopRating = ["basic-1", "basic-6"].includes(this.itemStyle);
    const showRowStarRating = ["basic-2", "basic-3", "basic-4", "basic-5"].includes(this.itemStyle);

    const srcset = this.imageUrl !== null && this.imageHighResolutionUrl !== null
      ? `${this.imageUrl} 640w, ${this.imageHighResolutionUrl} 1080w`
      : null;

    return (
      <Host class={`${this.itemStyle} ${this.reviewClass} ${this.starOnlyClass}`} is-loading={this.itemType !== "text"}
        style={this.getItemCSSVariables()} aria-hidden={`${this.tabIndex === -1}`}>
        <button class="btn-item-wrap" disabled={this.tabIndex === -1} onClick={this.itemClickHandler} onKeyDown={this.itemClickHandler} 
          onFocus={this.itemOnFocusHandler} onMouseEnter={this.itemOnHoverHandler} type="button" aria-label="сlick to open popup">
          <div class={`img-wrap ${this.itemStyle} ${this.layout} ${this.starOnlyClass}`}>
            {this.itemType !== "text" &&
              <img class={`img ${this.layout} ${this.layout === "slider" ? "bg" : ""}`}
                onLoad={this.imageLoadedHandler} onError={this.imageLoadedHandler}
                srcset={srcset}
                sizes={this.imageResponsiveSizes}
                loading="lazy"
                alt={this.altTag}
              />
            }
            {/* text-review caption */}
            {!hideCaption && this.itemType === "text" &&
              <covet-pics-crop-text 
                class={`text-review ${this.layout} ${this.itemStyle} ${this.verified ? "verified" : ""}`} // TODO check style for basic
                text={this.caption}
                text-class={`${this.itemStyle} ${this.verified ? "verified" : ""}`} // TODO check style for basic
                text-color={this.appState.settings.primary_color}
                item-style={this.itemStyle}
                ratio={basicRatio}
              ></covet-pics-crop-text>  
            }
            {/* star-only */}
            {starOnly &&
              <div class={`caption star-only ${this.itemStyle}`}>
                <StarRating 
                  rating={this.rating} 
                  ratingClass={`star-rating ${this.itemStyle} ${this.reviewClass}`}
                  starClass={`${this.itemStyle} ${this.reviewClass} ${this.starOnlyClass} ${this.highlightedClass}`}
                ></StarRating>
                <p class={`star-label ${this.itemStyle}`}>(Rated {this.rating} stars)</p>
              </div>
            }
            {this.verified && <BadgeVerified badgeClass={this.itemStyle} iconClass={`${this.itemStyle} ${this.itemType}`}/>}
          </div>
          <div class={`description ${this.itemStyle} ${this.layout} ${this.highlightedClass} ${this.reviewClass} ${this.starOnlyClass}`}>
            {showTopRating &&
              <div class={`top ${this.itemStyle}`}>
                {this.itemStyle === "basic-1" && this.userName && <span class="avatar">{this.userName[0]}</span>}
                {this.itemStyle === "basic-1" &&
                  <div class="top-wrap">
                    <h3 class="username basic-1">{this.userName}</h3>
                    <span class="created-time basic-1">{format(this.createdTime)}</span>
                  </div>
                }
                {this.renderRating()}
              </div>
            }
            {showRowStarRating && this.rating > 0 &&
              <div class={`top ${this.itemStyle} ${this.reviewClass} ${this.starOnlyClass}`}>
                <StarRating 
                  rating={this.rating} 
                  ratingClass={`star-rating ${this.itemStyle} ${this.reviewClass}`} 
                  starClass={`${this.itemStyle}`}
                ></StarRating>
              </div>
            }
            {!hideCaption && this.itemType !== "text" &&
              <p class={`caption ${this.layout} ${this.itemStyle} ${jsTextCut}`}
                style={this.captionCSS} innerHTML={this.caption}>
              </p>
            }
            {this.itemType === "text" &&
              <div class={`bottom-review ${this.itemStyle} ${this.layout} ${this.highlightedClass}`}>
                <span class={`link-label ${this.itemStyle}`}>Review For</span>
                <a href={this.tabIndex !== -1 ? this.links[0].link: null} class={`link-review ${this.itemStyle}`}>{this.links[0].title}</a>
              </div>
            }
            {this.itemStyle !== "basic-1" &&
              <div class={`bottom ${this.itemStyle} ${this.layout} ${this.highlightedClass} ${this.reviewClass} ${this.starOnlyClass}`}>
                {this.renderUserAndDate()}
              </div>
            }
          </div>
        </button>
      </Host>
    );
  }

  private renderImgModernItem() {
    const hideCaption = (this.caption === null) || this.caption.length === 0 && this.highlighted;
    const jsTextCut = Object.keys(this.captionCSS).length !== 0 ? "js-text-cut" : "";

    let showTopRating = ["modern-1", "modern-4", "modern-6"].includes(this.itemStyle);
    if (["modern-1", "modern-4", "modern-6"].includes(this.itemStyle) && this.rating === 0) showTopRating = false;
    const showRowStarRating = ["modern-2", "modern-3", "modern-5"].includes(this.itemStyle);

    const srcset = this.imageUrl !== null && this.imageHighResolutionUrl !== null
      ? `${this.imageUrl} 640w, ${this.imageHighResolutionUrl} 1080w`
      : null;

    return (
      <Host class={`${this.itemStyle}`} is-loading={this.itemType !== "text"} style={this.getItemCSSVariables()} aria-hidden={`${this.tabIndex === -1}`}>
        <button class="btn-item-wrap" disabled={this.tabIndex === -1} onClick={this.itemClickHandler} onKeyDown={this.itemClickHandler} 
          onFocus={this.itemOnFocusHandler} onMouseEnter={this.itemOnHoverHandler} type="button" aria-label="сlick to open popup">
          <div class={`img-wrap ${this.itemStyle}`}>
            <img class={`img ${this.layout} ${this.layout === "slider" ? "bg" : ""}`}
              onLoad={this.imageLoadedHandler} onError={this.imageLoadedHandler}
              srcset={srcset}
              sizes={this.imageResponsiveSizes}
              loading="lazy"
              alt={this.altTag}
            />
            {this.verified && this.itemStyle === "modern-3"  && 
              <BadgeVerified badgeClass={this.itemStyle} iconClass={`${this.itemStyle} ${this.itemType}`}/>
            }
          </div>
          <div class={`description ${this.itemStyle} ${this.layout} ${this.highlightedClass} ${this.verified ? "verified" : ""}`}>
            {this.verified && this.itemStyle === "modern-2"  && 
              <BadgeVerified badgeClass={this.itemStyle} iconClass={`${this.itemStyle} ${this.itemType}`}/>
            }
            <div class={`top ${this.itemStyle} ${this.itemType}`}>
              {showTopRating && this.renderRating()}
              {this.verified && this.itemStyle === "modern-1" && 
                <BadgeVerified badgeClass={this.itemStyle} iconClass={`${this.itemStyle} ${this.itemType}`}/>
              }
              {showRowStarRating && this.rating > 0 && 
                <StarRating 
                  rating={this.rating} 
                  ratingClass={`star-rating ${this.itemStyle} ${this.reviewClass}`} 
                  starClass={`${this.itemStyle}`}
                ></StarRating>
              }
            </div>

            {!hideCaption &&
              <p class={`caption ${this.layout} ${this.itemStyle} ${jsTextCut}`}
                style={this.captionCSS} innerHTML={this.caption}>
              </p>
            }
            <div class={`bottom ${this.itemStyle} ${this.layout} ${this.highlightedClass}`}>
              {this.verified && ["modern-4", "modern-5", "modern-6"].includes(this.itemStyle) && 
                <BadgeHoverVerified badgeClass={this.itemStyle} iconClass={this.itemStyle}/>
              }
              {this.renderUserAndDate()}
            </div>
          </div>
        </button>
      </Host>
    );
  }

  private renderReviewModernItem() {
    const hideCaption = (this.caption === null) || this.caption.length === 0 && this.highlighted;
    const textReview = this.caption !== null && this.itemType === "text";
    const starOnly = this.caption === null && this.itemType === "text"; // TODO need to refactor, move review split img-wrap and review to different render function

    const showRating = (textReview && ["modern-1","modern-4", "modern-6"].includes(this.itemStyle)) 
      || (starOnly && ["modern-1","modern-4", "modern-6"].includes(this.itemStyle));
    const showUserAndData = (textReview && ["modern-1","modern-4"].includes(this.itemStyle)) 
      || (starOnly && ["modern-1", "modern-2", "modern-3", "modern-4", "modern-5"].includes(this.itemStyle));
    const showStarRating = textReview && ["modern-2","modern-3","modern-5"].includes(this.itemStyle);
    const reviewColor = ["modern-1", "modern-2", "modern-3", "modern-4"].includes(this.itemStyle)
      ? "#000000"
      : this.appState.settings.primary_color;


    return (
      <Host class={`${this.itemStyle} ${this.reviewClass} ${this.starOnlyClass}`} is-loading={this.itemType !== "text"} style={this.getItemCSSVariables()} aria-hidden={`${this.tabIndex === -1}`}>
        <button class="btn-item-wrap" disabled={this.tabIndex === -1} onClick={this.itemClickHandler} onKeyDown={this.itemClickHandler} 
          onFocus={this.itemOnFocusHandler} onMouseEnter={this.itemOnHoverHandler} type="button" aria-label="сlick to open popup">
          <div class={`img-wrap ${this.itemStyle} ${this.reviewClass} ${this.starOnlyClass}`}></div>
          <div class={`description ${this.itemStyle} ${this.reviewClass} ${this.starOnlyClass} ${this.layout} ${this.highlightedClass}`}>
            <div class={`top ${this.itemStyle} ${this.highlightedClass} ${this.reviewClass} ${this.starOnlyClass}`}>
              {showRating && this.renderRating()}
              {this.verified && this.itemStyle === "modern-1" && 
                <BadgeHoverVerified badgeClass={this.itemStyle} iconClass={this.itemStyle}/>
              }
              {showUserAndData && this.renderUserAndDate()}
              {showStarRating &&
                <StarRating 
                  rating={this.rating} 
                  ratingClass={`star-rating ${this.itemStyle} ${this.reviewClass}`} 
                  starClass={`${this.itemStyle}`}
                ></StarRating>
              }
            </div>
            {starOnly &&
              <div class={`caption star-only ${this.itemStyle}`}>
                <StarRating 
                  rating={this.rating} 
                  ratingClass={`star-rating ${this.itemStyle} ${this.starOnlyClass}`} 
                  starClass={`star-only ${this.itemStyle}`}
                ></StarRating>
                <p class={`star-label ${this.itemStyle}`}>(Rated {this.rating} stars)</p>
              </div>
            }
            {!hideCaption &&
              <covet-pics-crop-text
                text={this.caption}
                item-style={this.itemStyle}
                text-color={reviewColor}
                ratio={modernRatio}
              ></covet-pics-crop-text>
            }
            {this.links && this.links.length > 0 &&
              <div class={`bottom-review ${this.itemStyle} ${this.layout} ${this.highlightedClass}`}>
                {this.verified && this.itemStyle !== "modern-1" && 
                  <BadgeHoverVerified badgeClass={this.itemStyle} iconClass={this.itemStyle}/>
                }
                <span class={`link-label ${this.itemStyle}`}>Review For</span>
                <a href={this.tabIndex !== -1 ? this.links[0].link: null} class={`link-review ${this.itemStyle}`}>{this.links[0].title}</a>
              </div>
            }
          </div>
        </button>
      </Host>
    );
  }

  private renderModernItem() {
    return this.itemType === "text"
      ? this.renderReviewModernItem()
      : this.renderImgModernItem();
  }

  render() {
    if (this.itemSource === "upload_widget") return this.renderUploadWidget();
    if (["basic-1", "basic-2", "basic-3", "basic-4", "basic-5", "basic-6"].includes(this.itemStyle))
      return this.renderBasicItem();

    if (["modern-1", "modern-2", "modern-3", "modern-4", "modern-5", "modern-6"].includes(this.itemStyle))
      return this.renderModernItem();

    return null;
  }

}

